/**
 * Route sequence: the cameras a track passed and the places it dwelled, in
 * the order they happened. Built client-side from the loaded track points,
 * the cameras currently drawn and the dwell rows, so it always matches the
 * map. Exportable as CSV for products.
 */
import type { CameraFeatureCollection } from './cameraOverlayService';
import { CAMERA_CATEGORY_COLORS, CAMERA_OVERLAY_COLOR, cameraCategoryLabel, cameraDisplayLabel } from './cameraOverlayService';

export const ROUTE_SEQUENCE_DWELL_COLOR = '#ff0000';

export function routeSequenceEntryColor(e: Pick<RouteSequenceEntry, 'kind' | 'category'>): string {
  if (e.kind === 'dwell') return ROUTE_SEQUENCE_DWELL_COLOR;
  return (e.category && CAMERA_CATEGORY_COLORS[e.category]) || CAMERA_OVERLAY_COLOR;
}
import { PointGrid, type TaggedPoint } from './cameraProximity';

export type RouteSequenceKind = 'camera' | 'dwell';

export type RouteSequencePoint = {
  lon: number;
  lat: number;
  time: Date;
  entityId: string;
  deviceId?: string;
  deviceType?: string;
};

export type RouteSequenceDwell = {
  id: number | string;
  entityId: string;
  deviceType?: string;
  lon: number;
  lat: number;
  start: Date;
  end: Date;
  durationS: number;
  placeName?: string | null;
  address?: string | null;
};

export type RouteSequenceEntry = {
  /** 1-based position within the entity's sequence. */
  seq: number;
  kind: RouteSequenceKind;
  entityId: string;
  deviceId?: string;
  deviceType?: string;
  /** Camera: first pass within range. Dwell: arrival. */
  time: Date;
  /** Camera: last point of that pass. Dwell: departure. */
  endTime: Date;
  /** Camera display label (intersection / name / id) or dwell place name / coordinates. */
  label: string;
  /** Short secondary text for the overlay. */
  detail: string;
  lon: number;
  lat: number;
  cameraId?: string;
  category?: string | null;
  categoryLabel?: string;
  /** Camera: nearest track point during the pass (m). */
  distanceM?: number;
  /** Camera: how many track points fell within range on this pass. */
  hits?: number;
  /** Dwell: seconds. */
  durationS?: number;
  address?: string | null;
};

export type RouteSequenceGroup = {
  entityId: string;
  entries: RouteSequenceEntry[];
};

export type RouteSequenceOptions = {
  points: RouteSequencePoint[];
  cameras: CameraFeatureCollection | null;
  /** Metres from a track point for a camera to count as passed. */
  radiusM: number;
  dwells: RouteSequenceDwell[];
  /** Gap between in-range points that splits one camera into two passes. Default 10 min. */
  passGapMs?: number;
};

const DEFAULT_PASS_GAP_MS = 10 * 60_000;

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/**
 * Build per-entity sequences. Entities are ordered by their first event;
 * entries within an entity by time. A camera passed twice (out and back)
 * appears twice, each at its own position.
 */
export function buildRouteSequence(opts: RouteSequenceOptions): RouteSequenceGroup[] {
  const passGap = opts.passGapMs ?? DEFAULT_PASS_GAP_MS;
  const byEntity = new Map<string, RouteSequenceEntry[]>();
  const push = (e: RouteSequenceEntry) => {
    const list = byEntity.get(e.entityId);
    if (list) list.push(e);
    else byEntity.set(e.entityId, [e]);
  };

  // Cameras: find track points in range, split into passes per entity.
  const points = opts.points.filter(
    (p) => Number.isFinite(p.lon) && Number.isFinite(p.lat) && p.time instanceof Date && !Number.isNaN(p.time.getTime())
  );
  if (opts.cameras && opts.cameras.features.length && points.length) {
    const grid = new PointGrid<RouteSequencePoint>(
      points.map((p): TaggedPoint<RouteSequencePoint> => ({ lon: p.lon, lat: p.lat, tag: p })),
      opts.radiusM
    );
    for (const feature of opts.cameras.features) {
      const coords = feature.geometry?.coordinates;
      if (!Array.isArray(coords)) continue;
      const lon = Number(coords[0]);
      const lat = Number(coords[1]);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
      const near = grid.within(lon, lat, opts.radiusM);
      if (!near.length) continue;
      const props = feature.properties;
      const label = cameraDisplayLabel(props);
      const categoryLabel = props.category ? cameraCategoryLabel(props.category) : 'Camera';

      // Group hits by entity, then split into passes by time gap.
      const hitsByEntity = new Map<string, Array<{ p: RouteSequencePoint; d: number }>>();
      for (const m of near) {
        const list = hitsByEntity.get(m.tag.entityId);
        const hit = { p: m.tag, d: m.distanceM };
        if (list) list.push(hit);
        else hitsByEntity.set(m.tag.entityId, [hit]);
      }
      hitsByEntity.forEach((hits, entityId) => {
        hits.sort((a, b) => a.p.time.getTime() - b.p.time.getTime());
        let start = 0;
        for (let i = 1; i <= hits.length; i++) {
          const boundary = i === hits.length || hits[i].p.time.getTime() - hits[i - 1].p.time.getTime() > passGap;
          if (!boundary) continue;
          const pass = hits.slice(start, i);
          start = i;
          let best = pass[0];
          for (const h of pass) if (h.d < best.d) best = h;
          push({
            seq: 0,
            kind: 'camera',
            entityId,
            deviceId: best.p.deviceId,
            deviceType: best.p.deviceType,
            time: pass[0].p.time,
            endTime: pass[pass.length - 1].p.time,
            label,
            detail: `${categoryLabel} · ${Math.round(best.d)} m`,
            lon,
            lat,
            cameraId: props.camera_id,
            category: props.category ?? null,
            categoryLabel,
            distanceM: best.d,
            hits: pass.length,
          });
        }
      });
    }
  }

  // Dwells: one entry each.
  for (const d of opts.dwells) {
    if (!Number.isFinite(d.lon) || !Number.isFinite(d.lat)) continue;
    if (!(d.start instanceof Date) || Number.isNaN(d.start.getTime())) continue;
    const end = d.end instanceof Date && !Number.isNaN(d.end.getTime()) ? d.end : d.start;
    push({
      seq: 0,
      kind: 'dwell',
      entityId: d.entityId,
      deviceType: d.deviceType,
      time: d.start,
      endTime: end,
      label: (d.placeName || '').trim() || `${d.lat.toFixed(5)}, ${d.lon.toFixed(5)}`,
      detail: `Dwell · ${formatDuration(d.durationS)}`,
      lon: d.lon,
      lat: d.lat,
      durationS: d.durationS,
      address: d.address ?? null,
    });
  }

  const groups: RouteSequenceGroup[] = [];
  byEntity.forEach((entries, entityId) => {
    entries.sort((a, b) => a.time.getTime() - b.time.getTime() || (a.kind === 'dwell' ? -1 : 1));
    entries.forEach((e, i) => {
      e.seq = i + 1;
    });
    groups.push({ entityId, entries });
  });
  groups.sort((a, b) => a.entries[0].time.getTime() - b.entries[0].time.getTime());
  return groups;
}

function csvCell(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function fmtTime(d: Date, timeZone?: string): string {
  try {
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone,
      timeZoneName: 'short',
    });
  } catch {
    return d.toISOString();
  }
}

export const ROUTE_SEQUENCE_CSV_COLUMNS = [
  'Seq',
  'Entity',
  'Device',
  'Device type',
  'Type',
  'Time',
  'End',
  'Name / location',
  'Category',
  'Camera ID',
  'Address',
  'Distance (m)',
  'Duration',
  'Track hits',
  'Latitude',
  'Longitude',
  'Time (ISO)',
  'End (ISO)',
] as const;

export function routeSequenceToCsv(groups: RouteSequenceGroup[], timeZone?: string): string {
  const lines: string[] = [ROUTE_SEQUENCE_CSV_COLUMNS.join(',')];
  for (const g of groups) {
    for (const e of g.entries) {
      lines.push(
        [
          e.seq,
          e.entityId,
          e.deviceId ?? '',
          e.deviceType ?? '',
          e.kind === 'camera' ? 'Camera' : 'Dwell',
          fmtTime(e.time, timeZone),
          fmtTime(e.endTime, timeZone),
          e.label,
          e.kind === 'camera' ? e.categoryLabel ?? '' : '',
          e.cameraId ?? '',
          e.address ?? '',
          e.distanceM != null ? Math.round(e.distanceM) : '',
          e.durationS != null ? formatDuration(e.durationS) : '',
          e.hits ?? '',
          e.lat.toFixed(6),
          e.lon.toFixed(6),
          e.time.toISOString(),
          e.endTime.toISOString(),
        ]
          .map(csvCell)
          .join(',')
      );
    }
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/** Tab-separated text for pasting straight into a slide / doc table. */
export function routeSequenceToTsv(groups: RouteSequenceGroup[], timeZone?: string): string {
  const rows: string[] = [['Seq', 'Entity', 'Type', 'Time', 'End', 'Name / location', 'Detail'].join('\t')];
  for (const g of groups) {
    for (const e of g.entries) {
      rows.push(
        [
          e.seq,
          e.entityId,
          e.kind === 'camera' ? 'Camera' : 'Dwell',
          fmtTime(e.time, timeZone),
          fmtTime(e.endTime, timeZone),
          e.label,
          e.kind === 'camera' ? `${e.categoryLabel ?? ''}${e.cameraId ? ` ${e.cameraId}` : ''} · ${Math.round(e.distanceM ?? 0)} m` : `${formatDuration(e.durationS ?? 0)}${e.address ? ` · ${e.address}` : ''}`,
        ]
          .map((v) => String(v ?? '').replace(/\t|\r?\n/g, ' '))
          .join('\t')
      );
    }
  }
  return rows.join('\n');
}

export function downloadTextFile(filename: string, text: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function routeSequenceFilename(groups: RouteSequenceGroup[]): string {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  const who =
    groups.length === 1 ? groups[0].entityId.replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40) : `${groups.length}-entities`;
  return `route-sequence_${who}_${stamp}.csv`;
}

/** GeoJSON of numbered badges for the map overlay. */
export function routeSequenceBadges(groups: RouteSequenceGroup[]): GeoJSON.FeatureCollection<GeoJSON.Point, Record<string, unknown>> {
  const features: GeoJSON.Feature<GeoJSON.Point, Record<string, unknown>>[] = [];
  for (const g of groups) {
    for (const e of g.entries) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [e.lon, e.lat] },
        properties: {
          seq: e.seq,
          kind: e.kind,
          entity: g.entityId,
          label: e.label,
          color: routeSequenceEntryColor(e),
          sort: e.time.getTime(),
        },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}
